import React, { addReducer, useState, useDispatch, getGlobal, setGlobal } from "reactn"
import { graphql } from "gatsby"
import { createStorefrontApiClient } from "@shopify/storefront-api-client"
import { Global } from "@emotion/react"
import { ChakraProvider, extendTheme } from "@chakra-ui/react"
import myTheme from "../styles/theme"
import fonts from "../styles/fonts"
import { LeftDrawer } from "../components/navigation/drawer"
import { Header } from "../components/navigation/header"
import { Helmet } from "react-helmet"
import { setUser } from "../components/auth/auth"
import Logging from "../components/utils/logging"
import { Provider, ErrorBoundary } from "@rollbar/react"
import { createContext, useContext } from "react"
import { OnlineStatusProvider } from "../components/utils/onlineStatus"

// Import refactored modules
import { STOREFRONT_CONFIG, ROLLBAR_CONFIG } from "./constants"
import { cartEmpty, defaultValues } from "./initialState"
import { initializeStorage, getStoredCartData, saveState } from "./utils/storage"
import { updateCartTotals, calculateTotals } from "./utils/cartCalculations"
import { cartOpened } from "./analytics"
import { clearShipping, fetchCartState, warmUpCartLambda } from "./network"

// Import reducers
import { setPromoCode, clearPromoCode, setDiscount, clearDiscount } from "./reducers/promoReducers"
import { setExpressShipping, setShipping, setPackagingOption, setAddress, differentBilling } from "./reducers/shippingReducers"
import { paymentComplete } from "./reducers/paymentReducers"

const theme = extendTheme(myTheme)
let ls
let cartData

/**
 * Create Shopify Storefront API client
 * @returns {Object|null} Storefront API client or null if server-side
 */
const getClient = () => {
  if (typeof window === "undefined") return null
  return createStorefrontApiClient(STOREFRONT_CONFIG)
}

const client = getClient()

// Export getClient function for use in other components
export { getClient }

/**
 * Initialize and get store state
 * @returns {Object} Initial store state
 */
function getStore() {
  let initialState = {}

  // Initialize storage
  ls = initializeStorage()
  cartData = getStoredCartData()

  // Check if we're on the payment page - don't restore cart data on payment page
  const isOnPaymentPage = typeof window !== 'undefined' && window.location.pathname.includes('/payment')

  if (cartData && !isOnPaymentPage) {
    const cartJSON = JSON.parse(cartData)
    console.log("CART JSON", cartJSON)
    initialState = {
      ...defaultValues,
      ...cartEmpty,
      client,
      // Restore saved state
      scarfOrder: cartJSON.scarfOrder || {},
      links: cartJSON.links || [],
      scarves: cartJSON.scarves || {},
      scarfID: cartJSON.scarfID || "",
      sid: cartJSON.sid,
      countryChange: cartJSON.countryChange || false,
      cartAvailable: true,
      cartInitialised: false,
      promoCode: cartJSON.promoCode?.promo_code ? cartJSON.promoCode : cartEmpty.promoCode,
      ss: cartJSON.ss || {},
      differentBillingAddress: cartJSON.differentBillingAddress || false,
      cartState: cartJSON.cartState || cartEmpty.cartState,
      emailAddress: cartJSON.emailAddress || "",
      contactNumber: cartJSON.contactNumber || "",
      shipping_address: cartJSON.shipping_address || cartEmpty.shipping_address,
      fullName: cartJSON.fullName || "",
      shippingID: cartJSON.shippingID || "",
      packagingID: cartJSON.packagingID || "",
      billing_address: cartJSON.billing_address || {},
      paymentAmount: cartJSON.paymentAmount,
      orderData: cartJSON.orderData || {},
      itemsInCart: cartJSON.itemsInCart || false,
      cartProducts: cartJSON.cartProducts || [],
      cartTotal: cartJSON.cartTotal || {
        productQuantity: 0,
        totalPrice: 0,
        currencyId: "GBP",
        currencyFormat: "£",
      },
      totals: cartJSON.totals || {
        shipping: 0,
        discount: 0,
        items: 0,
        total: 0,
      },
      // Explicitly restore cartOpen from storage
      cartOpen: false
      // cartJSON.cartOpen !== undefined ? cartJSON.cartOpen : false,
    }

  } else {
    initialState = { ...defaultValues, ...cartEmpty, client }
  }

  return initialState
}

export const GlobalContext = createContext(getStore())

/**
 * Check if we're on the payment page
 * @returns {boolean} True if on payment page
 */
const isPaymentPage = () => {
  if (typeof window === 'undefined') return false
  return window.location.pathname.includes('/payment')
}

/**
 * Empty the cart
 * @param {Object} currentStore - Current store state
 * @returns {Object} New store state
 */
export function emptyCart(currentStore) {
  if (!currentStore) {
    throw new Error('emptyCart requires a currentStore parameter')
  }
  return { ...currentStore, ...cartEmpty }
}

/**
 * Main site context provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Site context provider
 */
export const SiteContext = ({ children }) => {
  const [store, setStore] = useState(useContext(GlobalContext))

  // Create a ref to hold the current store state
  const storeRef = React.useRef(store)
  storeRef.current = store

  // Sync ReactN global state with Context store on mount and when store changes
  React.useEffect(() => {
    setGlobal(store)
  }, [store])

  /**
   * Update global state
   * @param {Object} objectToUpdate - Object to update state with
   */
  const updateGlobal = async (objectToUpdate) => {
    // Don't pass store as second parameter - let updateAndSave use the current store
    updateAndSave(objectToUpdate)
  }

  /**
   * Update and save state
   * @param {Object} newData - New data to merge
   * @param {Object} currentStore - Current store state
   */
  const updateAndSave = async (newData, currentStore) => {
    const storeToUse = currentStore || storeRef.current

    // Preserve cartOpen state if not explicitly provided in newData or if it's undefined
    const currentCartOpen = storeToUse.cartOpen
    const dataToSave = {
      ...newData,
      cartOpen: (newData.hasOwnProperty('cartOpen') && newData.cartOpen !== undefined) ? newData.cartOpen : currentCartOpen
    }

    const newStore = { ...storeToUse, ...dataToSave }

    // Update storeRef immediately so it's available for next operations
    storeRef.current = newStore

    // Sync ReactN global state with Context store
    setGlobal(newStore)

    setStore(newStore)
    saveState(newStore)

    // Return a promise that resolves after state is updated and saved
    // Use a microtask to ensure React has processed the state update
    return Promise.resolve()
  }

  // Effect hooks for cart management
  React.useEffect(() => {
    if (store.cartInitialised && store.cartOpen) {
      cartOpened(store)
    }
  }, [store.cartOpen])


  React.useEffect(() => {
    if (isPaymentPage()) return
    if (store.cartInitialised) {
      fetchCart(false)
    }
  }, [store.shippingID])

  React.useEffect(() => {
    if (isPaymentPage()) return
    if (store.cartInitialised) {
      fetchCart(true)
    }
  }, [store.promoCode?.promo_code])

  React.useEffect(() => {
    if (isPaymentPage()) return
    if (store.cartInitialised && store.cartUpdating) {
      fetchCart(false)
    }
  }, [store.cartUpdating])

  React.useEffect(() => {
    if (store.sid) {
      setUser(store.sid)
    } else {
      setUser("")
    }
  }, [store.sid])

  // Removed automatic cart fetch on IP change - now only fetches when cart is opened
  // React.useEffect(() => {
  //   if (isPaymentPage()) return
  //   if (store.ip && !store.cartInitialised) {
  //     fetchCart(true)
  //   }
  // }, [store.ip])

  // Cart warming effect - runs once when the app loads to warm up the lambda
  React.useEffect(() => {
    if (isPaymentPage()) return
    let hasWarmedUp = false

    const warmUpCart = () => {
      if (!hasWarmedUp && typeof window !== 'undefined') {
        hasWarmedUp = true
        // Make a lightweight call to warm up the cart lambda
        // This doesn't affect the user's cart state, just ensures the lambda is warm
        setTimeout(() => {
          try {
            warmUpCartLambda()
          } catch (error) {
            // Silently handle errors - this is just for warming up
          }
        }, 2000) // 2 second delay to ensure everything is loaded
      }
    }

    warmUpCart()
  }, []) // Empty dependency array means this runs once on mount


  addReducer("fetchCart", (global, dispatch, force) => {
    if (isPaymentPage()) return
    fetchCartState(store, updateAndSave)
  })

  addReducer("setPromoCode", (global, dispatch, promocode) => {
    updateAndSave(setPromoCode(global, dispatch, promocode))
  })

  addReducer("clearPromoCode", (global, dispatch) => {
    updateAndSave(clearPromoCode(global, dispatch))
  })

  addReducer("differentBilling", (global, dispatch, differentBilling) => {
    updateAndSave(differentBilling(global, dispatch, differentBilling))
  })

  addReducer("setAddress", (global, dispatch, data) => {
    updateAndSave(setAddress(global, dispatch, data))
  })

  addReducer("setDiscount", (global, dispatch, discountData) => {
    updateAndSave(setDiscount(global, dispatch, discountData))
  })

  addReducer("setExpressShipping", (global, dispatch, shippingData) => {
    updateAndSave(setExpressShipping(global, dispatch, shippingData))
  })

  addReducer("setShipping", (global, dispatch, shippingData) => {
    updateAndSave(setShipping(global, dispatch, shippingData))
  })

  addReducer("setPackagingOption", (global, dispatch, packagingData) => {
    updateAndSave(setPackagingOption(global, dispatch, packagingData))
  })

  addReducer("clearDiscount", (global, dispatch, discountData) => {
    updateAndSave(clearDiscount(global, dispatch, discountData))
  })

  addReducer("clearShipping", (global, dispatch, shippingData) => {
    clearShipping(store)
  })

  addReducer("fetchShippingRates", (global, dispatch, addressData) => {
    // This reducer handles fetching shipping rates based on address
    updateAndSave({
      fetchingShipping: true,
      shippingError: null
    })
    // Fetch cart state which includes shipping rates
    fetchCartState(store, updateAndSave)
  })

  addReducer("changeSize", (global, dispatch, changedProduct) => {
    const result = changeSize(global, dispatch, changedProduct)
    updateCart(result.cartProducts)
  })


  addReducer("paymentComplete", async (global, dispatch, order) => {
    const result = paymentComplete(global, dispatch, order)
    if (Object.keys(result).length > 0) {
      await updateAndSave(result, global)
    }
  })

  addReducer("emptyCart", (global, dispatch) => {
    let empty = emptyCart(store)
    updateAndSave(empty)
  })

  addReducer('removeProduct', (global, dispatch, product) => {
    removeProduct(global, product)
  })

  addReducer('addToCart', (global, dispatch, product) => {
    const cartProducts = store.cartProducts || []
    console.log("STORE ADD TO CART", store, product)
    let productAlreadyInCart = false

    cartProducts.forEach((cp) => {
      if (cp.sku === product.sku) {
        cp.quantity += product.quantity
        productAlreadyInCart = true
      }
    })
    if (!productAlreadyInCart) {
      cartProducts.push(product)
    }

    updateCart(cartProducts)
  })

  const updateCart = (cartProducts) => {
    let productQuantity = cartProducts.reduce((sum, p) => {
      sum += p.quantity
      return sum
    }, 0)
    let totalPrice = cartProducts.reduce((sum, p) => {
      sum += p.price * p.quantity * 100
      return sum
    }, 0)

    if (productQuantity == 0) {
      const empty = emptyCart(store)
      updateAndSave(empty)
    } else {
      let cartTotal = {
        productQuantity,
        totalPrice,
        currencyId: 'GBP',
        currencyFormat: '£',
      }

      updateAndSave({
        cartProducts: cartProducts,
        cartOpen: true,
        showPayment: false,
        preparePayment: false,
        cartTotal: cartTotal,
        itemsInCart: productQuantity > 0,
        searchVisible: false,
        hitsRendered: false
      }, store)
    }
  }

  addReducer('changeProductQuantity', (global, dispatch, changedProduct) => {
    console.log('CHANGE PRODUCT QUANTITY', changedProduct)
    var cartProducts = store.cartProducts || []
    const product = cartProducts.find((p) => p.sku === changedProduct.sku)
    if (product) {
      product.quantity = changedProduct.quantity
      if (product.quantity <= 0) {
        removeProduct(store, product)
        return // removeProduct already calls updateCart
      }
    }
    updateCart(cartProducts)
  })

  const removeProduct = (global, product) => {
    var cartProducts = store.cartProducts || []
    const index = cartProducts.findIndex((p) => p.sku === product.sku)
    if (index >= 0) {
      cartProducts.splice(index, 1)
      updateCart(cartProducts)
    }
  }


  const fetchCart = useDispatch("fetchCart")

  return (
    <GlobalContext.Provider value={{ store, updateGlobal }}>
      <OnlineStatusProvider>
        <Helmet lang="en">
          <script
            type="text/javascript"
            src="https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
            async
          />
          <meta charSet="utf-8" />
          <meta httpEquiv="x-ua-compatible" content="ie=edge" />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1"
          />
        </Helmet>

        <ChakraProvider theme={theme}>
          <Provider config={ROLLBAR_CONFIG}>
            <ErrorBoundary>
              <Logging />
              <Global styles={fonts} />
              <Header store={store} updateGlobal={updateGlobal} />
              <LeftDrawer />
              {children}
            </ErrorBoundary>
          </Provider>
        </ChakraProvider>
      </OnlineStatusProvider>
    </GlobalContext.Provider>
  )
}

/**
 * Hook to access the global store
 * @returns {Object} Store context value
 */
export const useStore = () => {
  const store = useContext(GlobalContext)
  return store
}

export const query = graphql`
  fragment SiteData on DatoCmsSite {
    id
    globalSeo {
      siteName
      titleSuffix
      twitterAccount
      facebookPageUrl
      fallbackSeo {
        title
        description
        twitterCard
        image {
          url
          gatsbyImageData
          smartTags
        }
      }
    }
    faviconMetaTags {
      ...GatsbyDatoCmsFaviconMetaTags
    }
  }
`

// Export cartEmpty for use in other modules
export { cartEmpty }
